import { BitgoConfigCredentialsInput, BitgoAddressInfoSearchInput } from "./bitgo";
import { ChainalysisConfigCredentialsInput, ChainalysisAddressInfoSearchInput } from "./chainalysis";
import { Chainalysis2AddressInfoSearchInput } from "./chainalysis2";
import { CoinfirmConfigCredentialsInput, CoinfirmAddressInfoSearchInput } from "./coinfirm";
import { ComplyAdvantageConfigCredentialsInput, ComplyAdvantageSanctionSearchInput } from "./complyadvantage";
import { CrystalConfigCredentialsInput, CrystalAddressInfoSearchInput } from "./crystal";
import { EllipticConfigCredentialsInput, EllipticAddressInfoSearchInput } from "./elliptic";
import { GTRConfigCredentialsInput } from "./gtr";
import { LexisNexisConfigCredentialsInput, LexisNexisSanctionSearchInput } from "./lexisnexis";
import { MastercardCryptoCredentialsConfigInput } from "./mastercardCryptoCredentials";
import { MerkleScienceConfigCredentialsInput, MerkleScienceAddressInfoSearchInput } from "./merklescience";
import { RefinitivConfigCredentialsInput, RefinitivSanctionSearchInput } from "./refinitiv";
import { ScorechainConfigCredentialsInput } from "./scorechain";
import { TRMConfigCredentialsInput, TRMAddressInfoSearchInput } from "./trm";
import { TRMSanctionsConfigCredentialsInput, TRMSanctionsSanctionSearchInput } from "./trmsanctions";

import { RiskScoreRuleSource, SanctionsListRuleSource } from "~/templates/rulesEditor/types";

// eslint-disable-next-line no-shadow
export enum IntegrationTypes {
  BLOCKCHAIN_ANALYTICS = "BLOCKCHAIN_ANALYTICS",
  SANCTION_SCREENING = "SANCTION_SCREENING",
  CRYPTO_CREDENTIAL = "CRYPTO_CREDENTIAL",
}

// eslint-disable-next-line no-shadow
export enum IntegrationIDs {
  BITGO = "BITGO",
  CHAINALYSIS = "CHAINALYSIS",
  CHAINALYSIS2 = "CHAINALYSIS2",
  COINFIRM = "COINFIRM",
  COMPLYADVANTAGE = "COMPLYADVANTAGE",
  CRYSTAL = "CRYSTAL",
  ELLIPTIC = "ELLIPTIC",
  LEXISNEXIS = "LEXISNEXIS",
  MERKLESCIENCE = "MERKLESCIENCE",
  REFINITIV = "REFINITIV",
  TRM = "TRM",
  TRMSANCTIONS = "TRMSANCTIONS",
  SCORECHAIN = "SCORECHAIN",
  MASTERCARD_CRYPTOCREDENTIALS = "MASTERCARD-CRYPTO-CREDENTIALS",
  GTR = "GTR",
}

// Unfortunately the naming on the BE's Provider table and the transaction_rule table are not entirely consistent.
export const IntegrationIdsToIntegrationRuleSource: Record<
  Partial<IntegrationIDs>,
  RiskScoreRuleSource | SanctionsListRuleSource | "BitGo" | "MasterCard" | "GTR"
> = {
  [IntegrationIDs.BITGO]: "BitGo", //make ts happy, not used in rules
  [IntegrationIDs.CHAINALYSIS]: RiskScoreRuleSource.CHAINALYSIS,
  [IntegrationIDs.CHAINALYSIS2]: RiskScoreRuleSource.CHAINALYSIS2,
  [IntegrationIDs.COINFIRM]: RiskScoreRuleSource.COINFIRM,
  [IntegrationIDs.CRYSTAL]: RiskScoreRuleSource.CRYSTAL,
  [IntegrationIDs.ELLIPTIC]: RiskScoreRuleSource.ELLIPTIC,
  [IntegrationIDs.TRM]: RiskScoreRuleSource.TRM,
  [IntegrationIDs.MERKLESCIENCE]: RiskScoreRuleSource.MERKLESCIENCE,
  [IntegrationIDs.SCORECHAIN]: RiskScoreRuleSource.SCORECHAIN,
  [IntegrationIDs.LEXISNEXIS]: SanctionsListRuleSource.LEXIS_NEXIS,
  [IntegrationIDs.COMPLYADVANTAGE]: SanctionsListRuleSource.COMPLY_ADVANTAGE,
  [IntegrationIDs.REFINITIV]: SanctionsListRuleSource.REFINITIV,
  [IntegrationIDs.TRMSANCTIONS]: SanctionsListRuleSource.TRM_SANCTIONS,
  [IntegrationIDs.GTR]: "GTR",
  [IntegrationIDs.MASTERCARD_CRYPTOCREDENTIALS]: "MasterCard", //make ts happy, not used in rules
};

// Define a mapping object for human-readable names
export const IntegrationDisplayNames: Record<IntegrationIDs, string> = {
  [IntegrationIDs.BITGO]: "BitGo",
  [IntegrationIDs.CHAINALYSIS]: "Chainalysis",
  [IntegrationIDs.CHAINALYSIS2]: "Chainalysis V2",
  [IntegrationIDs.COINFIRM]: "Coinfirm",
  [IntegrationIDs.COMPLYADVANTAGE]: "ComplyAdvantage",
  [IntegrationIDs.CRYSTAL]: "Crystal",
  [IntegrationIDs.ELLIPTIC]: "Elliptic",
  [IntegrationIDs.LEXISNEXIS]: "LexisNexis",
  [IntegrationIDs.MERKLESCIENCE]: "Merkle Science",
  [IntegrationIDs.REFINITIV]: "Refinitiv",
  [IntegrationIDs.TRM]: "TRM",
  [IntegrationIDs.TRMSANCTIONS]: "TRM Sanctions",
  [IntegrationIDs.SCORECHAIN]: "Scorechain",
  [IntegrationIDs.MASTERCARD_CRYPTOCREDENTIALS]: "Mastercard CryptoCredential",
  [IntegrationIDs.GTR]: "GTR Credentials",
};

export interface ConfigCredentialsInput {
  data:
    | BitgoConfigCredentialsInput
    | ChainalysisConfigCredentialsInput
    | CoinfirmConfigCredentialsInput
    | ComplyAdvantageConfigCredentialsInput
    | CrystalConfigCredentialsInput
    | EllipticConfigCredentialsInput
    | LexisNexisConfigCredentialsInput
    | MerkleScienceConfigCredentialsInput
    | RefinitivConfigCredentialsInput
    | TRMConfigCredentialsInput
    | TRMSanctionsConfigCredentialsInput
    | GTRConfigCredentialsInput
    | MastercardCryptoCredentialsConfigInput
    | ScorechainConfigCredentialsInput;
}

export interface ConfigCredentialsResponse {
  hasCredentialsBeenStored: boolean;
  message: string;
}

export interface HasCredentialsInput {
  vaspDID: string;
  integrationId: IntegrationIDs;
}

export interface DeleteCredentialsInput {
  vaspDID: string;
  integrationId: IntegrationIDs;
}

export interface DeleteCredentialsResponse {
  hasCredentialsBeenDeleted: boolean;
  message: string;
}

export interface AddressInfoSearchInput {
  vaspDID: string;
  integrationId: IntegrationIDs;
  data:
    | BitgoAddressInfoSearchInput
    | ChainalysisAddressInfoSearchInput
    | Chainalysis2AddressInfoSearchInput
    | CoinfirmAddressInfoSearchInput
    | CrystalAddressInfoSearchInput
    | EllipticAddressInfoSearchInput
    | MerkleScienceAddressInfoSearchInput
    | TRMAddressInfoSearchInput;
}

export interface AddressInfoSearchResponse {
  address: string;
  asset: string;
  addressType: "UNKNOWN" | "HOSTED" | "UNHOSTED";
  source:
    | "UNKNOWN"
    | "ADDRESS_GRAPH"
    | "CHAINALYSIS"
    | "ELLIPTIC"
    | "BITGO"
    | "TRM"
    | "COINFIRM"
    | "CRYSTAL"
    | "MERKLE_SCIENCE"
    | "SCORECHAIN";
  blockchain?: string;
  customerDID?: string;
  custodialVASPdid?: string;
  custodialVASPname?: string;
  ownershipProof?: {
    type: string;
    proof: string;
  };
  metadata?: any;
  createdAt: Date;
  score?: string;
}

export interface SanctionSearchInput {
  vaspDID: string;
  integrationId: IntegrationIDs;
  data:
    | ComplyAdvantageSanctionSearchInput
    | LexisNexisSanctionSearchInput
    | RefinitivSanctionSearchInput
    | TRMSanctionsSanctionSearchInput;
}

export interface SanctionSearchResponse {
  hasMatches: boolean;
  matches: {
    name: string;
    score: string;
    reasons: string[];
    comments: string[];
    metadata: any[];
  }[];
  metadata?: any;
  createdAt: Date;
}
